import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeads } from '@/hooks/useLeads';
import { useDeals } from '@/hooks/useDeals';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useUnifiedMetrics } from '@/hooks/useUnifiedMetrics';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { generateContextualResponse, detectQuestionType } from '@/lib/aiResponseEngine';
import { generatePriorityActions } from '@/lib/decisionEngine';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Zap,
  Users,
  Kanban,
  Calendar,
  BarChart3,
  Settings,
  Plus,
  Clock,
  TrendingUp,
  User,
  Phone,
  MessageCircle,
  DollarSign,
  Upload,
  Sparkles,
  AlertTriangle,
  Building2,
  MessageSquare,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';

interface CommandPaletteProps {
  onCreateLead?: () => void;
  onAddDeal?: () => void;
  onImportCSV?: () => void;
  onCreateWorkspace?: () => void;
}

const pages = [
  { name: 'Today', path: '/app', icon: Zap, keywords: ['home', 'dashboard', 'decisions', 'command'] },
  { name: 'Leads', path: '/app/leads', icon: Users, keywords: ['contacts', 'prospects'] },
  { name: 'Revenue Flow', path: '/app/pipeline', icon: Kanban, keywords: ['deals', 'stages', 'kanban', 'revenue', 'pipeline'] },
  { name: 'Calendar', path: '/app/calendar', icon: Calendar, keywords: ['schedule', 'followups', 'events'] },
  { name: 'Keep Warm', path: '/app/keep-warm', icon: MessageSquare, keywords: ['nurture', 'followup', 'warm'] },
  { name: 'Templates', path: '/app/templates', icon: FileText, keywords: ['message', 'whatsapp', 'template', 'keep warm'] },
  { name: 'Dashboard', path: '/app/dashboard', icon: BarChart3, keywords: ['analytics', 'reports', 'metrics'] },
  { name: 'Settings', path: '/app/settings', icon: Settings, keywords: ['preferences', 'account'] },
];

const quickActions = [
  { name: 'Add Lead', action: 'create-lead', icon: Plus, keywords: ['new', 'add', 'lead', 'contact'] },
  { name: 'Import CSV', action: 'import-csv', icon: Upload, keywords: ['upload', 'bulk', 'import'] },
  { name: 'Log Activity', action: 'log-activity', icon: Clock, keywords: ['activity', 'note', 'log'] },
];

const aiActions = [
  { name: 'What should I do today?', prompt: 'What should I focus on today?', icon: Zap, keywords: ['today', 'focus', 'priority', 'action'] },
  { name: 'Most at risk?', prompt: 'Which deal is most at risk?', icon: AlertTriangle, keywords: ['risk', 'at risk', 'danger', 'urgent'] },
  { name: 'Close €10k faster?', prompt: 'How can I close €10k faster?', icon: DollarSign, keywords: ['close', 'faster', 'revenue', '10k', 'money'] },
];

export function CommandPalette({ onCreateLead, onAddDeal, onImportCSV, onCreateWorkspace }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { data: leads } = useLeads();
  const { data: deals } = useDeals();
  const { workspace } = useWorkspace();
  const { metrics } = useUnifiedMetrics();
  const { workspaceLimit, plan } = useEntitlements();
  const { workspaces, refetchWorkspaces } = useWorkspaceScope();

  // Open on ⌘K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const runCommand = useCallback((command: () => void) => {
    setOpen(false);
    command();
  }, []);

  const handlePageSelect = (path: string) => {
    runCommand(() => navigate(path));
  };

  const handleActionSelect = (action: string) => {
    switch (action) {
      case 'create-lead':
        if (onCreateLead) runCommand(() => onCreateLead());
        break;
      case 'add-deal':
        if (onAddDeal) runCommand(() => onAddDeal());
        break;
      case 'import-csv':
        if (onImportCSV) runCommand(() => onImportCSV());
        break;
      case 'log-activity':
        runCommand(() => navigate('/app/leads'));
        break;
      case 'create-workspace':
        if (onCreateWorkspace) runCommand(() => onCreateWorkspace());
        break;
    }
  };

  const handleAIAction = (prompt: string) => {
    if (!workspace?.id || !leads || !deals || !metrics) {
      toast.error('Loading data...');
      return;
    }

    // Navigate to Assistant page with prompt
    runCommand(() => {
      navigate('/app/assistant', { state: { initialPrompt: prompt } });
    });
  };

  const handleLeadSelect = (leadId: string) => {
    runCommand(() => navigate(`/app/leads?id=${leadId}`));
  };

  const handleCallLead = (leadId: string) => {
    const lead = leads?.find(l => l.id === leadId);
    if (lead?.phone) {
      runCommand(() => window.open(`tel:${lead.phone}`, '_self'));
    }
  };

  const handleWhatsAppLead = (leadId: string) => {
    const lead = leads?.find(l => l.id === leadId);
    if (lead?.phone) {
      const cleanPhone = lead.phone.replace(/\D/g, '');
      const message = encodeURIComponent(`Hi ${lead.name}, following up on our conversation.`);
      runCommand(() => window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank'));
    }
  };

  // Filter leads/deals based on search query
  const filteredLeads = useMemo(() => {
    if (!leads) return [];
    if (!searchQuery.trim()) return leads.slice(0, 10); // Show top 10 if no search
    
    const query = searchQuery.toLowerCase();
    return leads.filter(l =>
      l.name.toLowerCase().includes(query) ||
      l.email?.toLowerCase().includes(query) ||
      l.company?.toLowerCase().includes(query) ||
      l.phone?.includes(query)
    ).slice(0, 10);
  }, [leads, searchQuery]);

  const filteredDeals = useMemo(() => {
    if (!deals) return [];
    if (!searchQuery.trim()) return deals.slice(0, 10);
    
    const query = searchQuery.toLowerCase();
    return deals.filter(d => {
      const lead = leads?.find(l => l.id === d.lead_id);
      return lead?.name.toLowerCase().includes(query) ||
        lead?.company?.toLowerCase().includes(query);
    }).slice(0, 10);
  }, [deals, leads, searchQuery]);

  // Filter only open leads with phone for quick contact
  const contactableLeads = leads?.filter(l =>
    l.pipeline_stage !== 'won' &&
    l.pipeline_stage !== 'lost' &&
    l.phone
  ).slice(0, 5) || [];

  // Hot leads first
  const hotLeads = leads?.filter(l =>
    l.temperature === 'hot' &&
    l.pipeline_stage !== 'won' &&
    l.pipeline_stage !== 'lost'
  ).slice(0, 5) || [];

  // Check if can create workspace
  const canCreateWorkspace = workspaces.length < workspaceLimit;

  // Reset search when closing
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearchQuery('');
    }
  };

  return (
    <CommandDialog open={open} onOpenChange={handleOpenChange}>
      <CommandInput 
        placeholder="Search leads, deals, pages, or actions..." 
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* AI Actions */}
        <CommandGroup heading="AI Assistant">
          {aiActions.map((action) => (
            <CommandItem
              key={action.prompt}
              onSelect={() => handleAIAction(action.prompt)}
              className="gap-2"
            >
              <action.icon className="w-4 h-4 text-primary" />
              <span>{action.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {/* Quick Actions */}
        <CommandGroup heading="Quick Actions">
          {quickActions.map((action) => (
            <CommandItem
              key={action.action}
              onSelect={() => handleActionSelect(action.action)}
              className="gap-2"
            >
              <action.icon className="w-4 h-4 text-muted-foreground" />
              <span>{action.name}</span>
            </CommandItem>
          ))}
          {canCreateWorkspace && onCreateWorkspace && (
            <CommandItem
              onSelect={() => handleActionSelect('create-workspace')}
              className="gap-2"
            >
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <span>Create Workspace</span>
            </CommandItem>
          )}
        </CommandGroup>

        <CommandSeparator />

        {/* Navigate */}
        <CommandGroup heading="Navigate">
          {pages.map((page) => (
            <CommandItem
              key={page.path}
              onSelect={() => handlePageSelect(page.path)}
              className="gap-2"
            >
              <page.icon className="w-4 h-4 text-muted-foreground" />
              <span>{page.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {/* Search Results: Leads */}
        {searchQuery.trim() && filteredLeads.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Leads">
              {filteredLeads.map((lead) => (
                <CommandItem
                  key={`lead-${lead.id}`}
                  onSelect={() => handleLeadSelect(lead.id)}
                  className="gap-2"
                >
                  <User className="w-4 h-4 text-muted-foreground" />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="truncate">{lead.name}</span>
                    {lead.company && (
                      <span className="text-xs text-muted-foreground truncate">{lead.company}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {/* Search Results: Deals */}
        {searchQuery.trim() && filteredDeals.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Deals">
              {filteredDeals.map((deal) => {
                const lead = leads?.find(l => l.id === deal.lead_id);
                return (
                  <CommandItem
                    key={`deal-${deal.id}`}
                    onSelect={() => navigate(`/app/pipeline`)}
                    className="gap-2"
                  >
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="truncate">{lead?.name || 'Unknown'}</span>
                      {deal.value && (
                        <span className="text-xs text-muted-foreground">
                          {deal.value.toLocaleString('en-US', { style: 'currency', currency: 'EUR' })}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}

        {hotLeads.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="🔥 Hot Leads - Quick Contact">
              {hotLeads.map((lead) => (
                <CommandItem
                  key={`hot-${lead.id}`}
                  className="gap-2"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <User className="w-4 h-4 text-destructive" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="truncate">{lead.name}</span>
                      {lead.company && (
                        <span className="text-xs text-muted-foreground truncate">{lead.company}</span>
                      )}
                    </div>
                    {lead.phone && (
                      <div className="flex gap-1">
                        <button
                          className="p-1.5 rounded hover:bg-primary/10 hover:text-primary transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCallLead(lead.id);
                          }}
                        >
                          <Phone className="w-3.5 h-3.5" />
                        </button>
                        <button
                          className="p-1.5 rounded hover:bg-[#25D366]/10 hover:text-[#25D366] transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleWhatsAppLead(lead.id);
                          }}
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {contactableLeads.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Quick Contact">
              {contactableLeads.filter(l => !hotLeads.some(h => h.id === l.id)).map((lead) => (
                <CommandItem
                  key={`contact-${lead.id}`}
                  className="gap-2"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="truncate">{lead.name}</span>
                      {lead.company && (
                        <span className="text-xs text-muted-foreground truncate">{lead.company}</span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button
                        className="p-1.5 rounded hover:bg-primary/10 hover:text-primary transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCallLead(lead.id);
                        }}
                      >
                        <Phone className="w-3.5 h-3.5" />
                      </button>
                      <button
                        className="p-1.5 rounded hover:bg-[#25D366]/10 hover:text-[#25D366] transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleWhatsAppLead(lead.id);
                        }}
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
